"use client";

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@speakai/ui/dialog";
import { Button } from "@speakai/ui/button";
import { useToast } from "@speakai/ui/toast";
import { trackFailure } from "@/lib/integrations/sentry-tracking";
import { pushDataLayer } from "@/lib/integrations/ga4";
import { subscriptionKeys, paymentKeys, dashboardKeys } from "@/queries/keys";
import { SelectSource } from "./select-source";
import {
  useRetrySubscriptionStripe,
  useUpdatePaymentMethod,
} from "../mutations";
import { classifyStripeError } from "../payment/stripe";
import type { UserSubscription } from "../types";

interface RetryPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  subscription: UserSubscription;
  canManageBilling: boolean;
}

/**
 * Cards-only picker shown when a subscription is in `pendingPayment`.
 * Selected card is set as default before the retry intent fetches its
 * clientSecret, so the server uses the chosen card instead of the
 * previously declined default.
 */
export function RetryPaymentDialog({
  open,
  onClose,
  subscription,
  canManageBilling,
}: RetryPaymentDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [selectedSourceId, setSelectedSourceId] = useState<string | undefined>(
    undefined,
  );

  const retryStripe = useRetrySubscriptionStripe();
  const updatePaymentMethod = useUpdatePaymentMethod();
  const isProcessing =
    retryStripe.isPending || updatePaymentMethod.isPending;

  const total = subscription.cost?.total ?? 0;
  const totalLabel = `$${total.toLocaleString()}`;

  async function handleConfirm() {
    if (!selectedSourceId) {
      toast.error(t("PAYMENT.SELECT_CARD_REQUIRED", "Please select a card."));
      return;
    }

    try {
      await updatePaymentMethod.mutateAsync({
        sourceId: selectedSourceId,
        isDefault: true,
      });
    } catch (err: unknown) {
      const errMessage = err instanceof Error ? err.message : "";
      trackFailure("payment", "fail", {
        error_reason: errMessage.slice(0, 200),
        charge_type: "retry",
        payment_method: "card",
        failure_type: "set_default_failed",
      });
      pushDataLayer("Subscription", "PurchaseFail", undefined, undefined, { failure_type: "set_default_failed", payment_method: "card" });
      toast.error(t("CREATE_SUBSCRIPTION.FAILED_TITLE"));
      return;
    }

    try {
      await retryStripe.mutateAsync();
      await Promise.race([
        Promise.allSettled([
          queryClient.refetchQueries({ queryKey: subscriptionKeys.all }),
          queryClient.refetchQueries({ queryKey: paymentKeys.balance() }),
          queryClient.refetchQueries({ queryKey: dashboardKeys.all }),
        ]),
        new Promise<void>((resolve) => setTimeout(resolve, 30_000)),
      ]);
      // Behavioral retry-success marker — plan/billing_cycle not in scope here.
      pushDataLayer("Subscription", "PurchaseSuccess", undefined, undefined, { source: "retry", payment_method: "card" });
      onClose();
    } catch (err: unknown) {
      const { message, isCardError, failureType } = classifyStripeError(err);
      const userMessage = isCardError
        ? message || t("CREATE_SUBSCRIPTION.FAILED")
        : t("CREATE_SUBSCRIPTION.FAILED");
      trackFailure("payment", "fail", {
        error_reason: message.slice(0, 200),
        charge_type: "retry",
        payment_method: "card",
        failure_type: failureType,
      });
      pushDataLayer("Subscription", "PurchaseFail", undefined, undefined, { failure_type: failureType, payment_method: "card" });
      toast.error(userMessage);
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader>
        <h2 className="text-lg font-semibold">
          {t("PAYMENT.CONTINUE_PAYMENT", "Continue Payment")}
        </h2>
      </DialogHeader>
      <DialogBody>
        <p className="mb-4 text-sm text-muted-foreground">
          {t(
            "PAYMENT.CONTINUE_PAYMENT_DESC",
            "Your payment is pending. Select a card to complete the charge.",
          )}
        </p>
        <SelectSource
          onSelect={(s) => setSelectedSourceId(s.id)}
          selectedSourceId={selectedSourceId}
          showBalance={false}
          showPaypalConnect={false}
          autoSelectDefault
          canManageBilling={canManageBilling}
        />
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={isProcessing}>
          {t("ACTION.CANCEL", "Cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={
            isProcessing || !selectedSourceId || !canManageBilling
          }
        >
          {isProcessing
            ? t("ACTION.PROCESSING")
            : t("PAYMENT.CONTINUE_PAYMENT_CTA", "Continue payment {{total}}", {
                total: totalLabel,
              })}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
